// @ts-check

/**
 * @typedef {Object} PrescriptionCalendar
 * @property {string} administrateDate
 * @property {string} [administrateVisitationId]
 */

/**
 * @typedef {Object} PadPrescriptionContent
 * @property {PrescriptionCalendar[]} [prescriptionCalendars]
 */

/**
 * @typedef {Object} PadPrescription
 * @property {number|string} id
 * @property {Object} [medicament]
 * @property {Object} [diet]
 * @property {Object} [procedure]
 * @property {PadPrescriptionContent} [prescriptionContent]
 */

/**
 * @typedef {Object} DtEvent
 * @property {number|string} padPrescriptionId
 * @property {string} date
 * @property {string} time
 * @property {PadPrescription} [padPrescription]
 */

/**
 * @typedef {Object} PrescriptionItem
 * @property {string} [name] Optional name of the prescription item.
 */

/**
 * @typedef {Object} PadPrescriptionEvent
 * @property {string} key
 * @property {number|string} padPrescriptionId
 * @property {string} date
 * @property {string} time
 * @property {PrescriptionItem|null} medicament
 * @property {PrescriptionItem|null} diet
 * @property {PrescriptionItem|null} procedure
 */

import { generateCode } from '../calendarVisitSchedule.schema';

/**
 * Builds standardized padPrescriptionEvents objects from the original event.
 * @param {DtEvent} dtEvent - A single event data.
 * @param {string} [key] - The generated unique key (defaults to generateCode()).
 * @returns {PadPrescriptionEvent} Object of padPrescriptionEvent in the expected format.
 */
export function buildPadPrescriptionEvent(dtEvent, key = generateCode()) {
  const { padPrescriptionId, date, time, padPrescription } = dtEvent;
  const { medicament, diet, procedure } = padPrescription || {};
  return {
    key,
    padPrescriptionId,
    date,
    time,
    medicament: cleanEventObject(medicament),
    diet: cleanEventObject(diet),
    procedure: cleanEventObject(procedure)
  };
}

/**
 * Helper function to check if an object is null, undefined, or has only null/undefined properties.
 * @param {Record<string, any> | null | undefined} obj
 * @returns {Record<string, any> | null} The original object or null
 */
const cleanEventObject = obj => {
  if (!obj) return null;

  // Verifica se existe pelo menos um valor que não seja null ou undefined
  const hasValidContent = Object.values(obj).some(value => value !== null && value !== undefined);

  return hasValidContent ? obj : null;
};
